import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { Seo } from "../seo/Seo";
import { getService, SERVICES_SEO } from "../seo/services";
import { STATE_SERVICES } from "../seo/services";
import { STATES, SITE } from "../seo/states";
import { PageShell, CTASection } from "../components/PageShell";

export default function ServicePage() {
  const { serviceSlug } = useParams();
  const svc = getService(serviceSlug);
  if (!svc) return <Navigate to="/" replace />;

  const url = `${SITE}/services/${svc.slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: svc.name,
    provider: { "@type": "ProfessionalService", name: "Alexa Financial Multiservices", url: SITE },
    areaServed: "United States",
    url,
    description: svc.metaDescription,
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: svc.faqs.map((f) => ({
      "@type": "Question", name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <PageShell
      eyebrow={svc.category}
      title={svc.h1}
      subtitle={svc.intro}
      breadcrumb={[{ label: "Home", to: "/" }, { label: "Services" }, { label: svc.name }]}
    >
      <Seo title={svc.metaTitle} description={svc.metaDescription} keywords={svc.keywords} canonical={url} jsonLd={[jsonLd, faqLd]} />

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-[#050505]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-10">Why choose Alexa Financial for {svc.name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {svc.benefits.map((bft, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 2) * 0.05 }}
                className="glass rounded-2xl p-6 flex items-start gap-4"
              >
                <span className="mt-0.5 w-6 h-6 rounded-full bg-[#d4af37]/15 flex items-center justify-center flex-shrink-0">
                  <Check size={14} className="text-[#d4af37]" />
                </span>
                <span className="text-[#d4d4d4]">{bft}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-24 bg-[#080808]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-12">How our {svc.name} process works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {svc.process.map((p, i) => (
              <div key={i} className="glass rounded-2xl p-7">
                <div className="font-display text-4xl text-[#d4af37]/40">0{i + 1}</div>
                <h3 className="mt-4 font-display text-xl text-white">{p.title}</h3>
                <p className="mt-2 text-sm text-[#a3a3a3] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-[#050505]">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-10">{svc.name} — Frequently Asked Questions</h2>
          <div className="space-y-5">
            {svc.faqs.map((f, i) => (
              <div key={i} className="border-b border-white/10 pb-5">
                <h3 className="font-display text-lg md:text-xl text-white">{f.q}</h3>
                <p className="mt-2 text-[#a3a3a3] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-14 bg-[#080808] border-t border-white/10">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <h2 className="font-display text-2xl text-white mb-6">Related services</h2>
          <div className="flex flex-wrap gap-3">
            {svc.related.map((r) => {
              const rs = getService(r);
              return rs ? (
                <Link key={r} to={`/services/${rs.slug}`} className="group inline-flex items-center gap-2 glass rounded-full px-5 py-2.5 text-sm text-[#d4d4d4] hover:text-white hover:border-[#d4af37]/40 transition-colors">
                  {rs.name} <ArrowUpRight size={14} className="text-[#d4af37]" />
                </Link>
              ) : null;
            })}
          </div>
          {STATE_SERVICES.includes(svc.slug) && (
            <>
              <h2 className="font-display text-2xl text-white mt-10 mb-6">{svc.name} by state</h2>
              <div className="flex flex-wrap gap-2.5">
                {STATES.map((st) => (
                  <Link key={st.slug} to={`/services/${svc.slug}/${st.slug}`} className="text-sm text-[#a3a3a3] hover:text-[#d4af37] transition-colors border border-white/10 rounded-full px-4 py-2">
                    {svc.name} in {st.name}
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <CTASection text={`Ready to get started with ${svc.name}?`} />
    </PageShell>
  );
}
